import { useEffect, useMemo, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Loader2, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { formsApi } from '@/api/formsApi'
import {
  FORM_CATEGORY_LABELS,
  FORM_CATEGORY_COLORS,
} from '@/types/cgt'
import type { FormDefinition, FormCategory } from '@/types/cgt'

interface AddFormDialogProps {
  open: boolean
  onClose: () => void
  defaultCategory: FormCategory
  onSelect: (def: FormDefinition) => void
  loading?: boolean
}

// Per-user column layout. Name is locked — it is what the author picks. See
// src/components/table/index.ts for the pattern.
const ADD_FORM_TABLE_ID = 'procedure-add-form'

const ADD_FORM_COLUMNS: TableColumnDef[] = [
  { id: 'name', header: 'Name', locked: true, sortable: true },
  { id: 'formType', header: 'Form Type', sortable: true },
  { id: 'layout', header: 'Layout' },
  { id: 'version', header: 'Version', sortable: true },
]

// Column sorting (z8nrz7e4h6). Layout is the same constant on every row, so
// there is nothing to sort by.
const ADD_FORM_SORT: SortAccessors<FormDefinition> = {
  name: d => d.name,
  formType: d => FORM_CATEGORY_LABELS[d.category],
  version: d => d.version,
}

export function AddFormDialog({
  open,
  onClose,
  defaultCategory,
  onSelect,
  loading = false,
}: AddFormDialogProps) {
  const cols = useTableColumns(ADD_FORM_TABLE_ID, ADD_FORM_COLUMNS)
  const [defs, setDefs] = useState<FormDefinition[]>([])
  const [fetching, setFetching] = useState(false)
  const [query, setQuery] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [selected, setSelected] = useState<FormDefinition | null>(null)

  const categoryFilter = showAll ? undefined : defaultCategory

  useEffect(() => {
    if (!open) return
    setFetching(true)
    setSelected(null)
    if (!showAll) setQuery('')
    formsApi
      .listDefinitions({ category: categoryFilter, active: true })
      .then(({ items }) => setDefs(items))
      .catch(console.error)
      .finally(() => setFetching(false))
  }, [open, categoryFilter, showAll])

  const filtered = useMemo(() => {
    if (!query.trim()) return defs
    const q = query.toLowerCase()
    return defs.filter(d => d.name.toLowerCase().includes(q))
  }, [defs, query])
  const rows = useSortedRows(filtered, cols.sort, ADD_FORM_SORT)

  const title = defaultCategory === 'capa' ? 'Create CAPA' : 'Add Form'
  const description =
    defaultCategory === 'capa'
      ? 'Select a CAPA form to attach to this procedure.'
      : 'Select an exception form to attach to this procedure.'

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className='flex items-center gap-3'>
          <div className='relative flex-1'>
            <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <input
              type='text'
              placeholder='Search by name or form type...'
              value={query}
              onChange={e => setQuery(e.target.value)}
              className='h-9 w-full rounded-md border bg-background pl-9 pr-3 text-sm outline-none focus:ring-1 focus:ring-ring'
            />
          </div>
          <label className='flex items-center gap-1.5 text-sm text-muted-foreground'>
            <input
              type='checkbox'
              checked={showAll}
              onChange={e => setShowAll(e.target.checked)}
              className='rounded border-muted-foreground'
            />
            Show All
          </label>
        </div>

        <div className='max-h-72 overflow-auto rounded-md border'>
          {fetching ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          ) : filtered.length === 0 ? (
            <p className='py-12 text-center text-sm text-muted-foreground'>
              No forms found.
            </p>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                    >
                      {def.header}
                    </ResizableTableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(def => (
                  <TableRow
                    key={def.id}
                    className={cn(
                      'cursor-pointer',
                      selected?.id === def.id && 'bg-accent',
                    )}
                    onClick={() => setSelected(def)}
                  >
                    <ColumnCells
                      columns={cols}
                      cells={{
                        name: <TableCell className='font-medium'>{def.name}</TableCell>,
                        formType: (
                          <TableCell>
                            <span
                              className='inline-block rounded px-1.5 py-0.5 text-xs font-medium text-white'
                              style={{ backgroundColor: FORM_CATEGORY_COLORS[def.category] }}
                            >
                              {FORM_CATEGORY_LABELS[def.category]}
                            </span>
                          </TableCell>
                        ),
                        layout: (
                          <TableCell className='text-muted-foreground'>
                            Standard
                          </TableCell>
                        ),
                        version: (
                          <TableCell className='text-muted-foreground'>
                            V{def.version}
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            disabled={!selected || loading}
            onClick={() => selected && onSelect(selected)}
          >
            {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {defaultCategory === 'capa' ? 'Create CAPA' : 'Add Form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
